import { useEffect, useCallback, useRef } from "react";
import { addGuard } from "@/utils";
import { dataSyncEmitter } from "@/modules/events/emitter";
import { useCamera } from "camera/store/camera";
import NoiseDetector from "../noise/NoiseDetector";

export default function useMediaStream({ onStreamLoad }: { onStreamLoad: Cb }) {
  const { stream, setError, setLoading } = useCamera((state) => ({
    stream: state.stream,
    setError: state.setError,
    setLoading: state.setLoading
  }));
  const currentStreamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const onVideoLoad = useCallback(() => {
    log.media("Video loaded");
    setLoading(false);
    videoRef.current?.removeEventListener("loadeddata", onVideoLoad);
    onStreamLoad();
  }, [setLoading, onStreamLoad]);

  const updateCurrentStream = useCallback(async () => {
    if (!videoRef.current) {
      setError("no_video_element");
      return;
    }

    if (!currentStreamRef.current) {
      videoRef.current.addEventListener("loadeddata", onVideoLoad);
    }
    currentStreamRef.current = stream;
    videoRef.current.srcObject = stream;
    addGuard(() => videoRef.current!.play());
  }, [onVideoLoad, setError, stream]);

  useEffect(() => {
    if (stream && stream !== currentStreamRef.current) {
      updateCurrentStream();
      NoiseDetector.connectStream();
    }
  }, [stream, updateCurrentStream]);

  // When tracks are replaced on an existing stream, playback might not resume (Safari ofc).
  // Listen for track updates and force re-attach + play.
  useEffect(() => {
    const onVideoTrackUpdate = () => {
      if (!videoRef.current || !currentStreamRef.current) return;
      videoRef.current.srcObject = currentStreamRef.current;
      addGuard(() => videoRef.current!.play());
    };
    return dataSyncEmitter.on("video-track-update", onVideoTrackUpdate);
  }, []);

  return { videoRef };
}
